import type { ReactNode } from 'react';
import clsx from 'clsx';

export type MeterTone = 'ok' | 'warn' | 'danger' | 'muted';

interface MeterBarProps {
  label: string;
  /** El texto corto a la derecha: "Vence en 20 días", "Sin datos". */
  caption: string;
  /**
   * 0–1. **Si es `undefined` no se pinta el riel**, solo el texto: una barra
   * sin denominador honesto miente más que un renglón sin barra.
   */
  ratio?: number;
  tone: MeterTone;
  icon?: ReactNode;
  /** Línea de explicación bajo la barra (p. ej. el motivo de un "Sin datos"). */
  hint?: string;
}

/**
 * Medidor de una línea: etiqueta + texto y, opcionalmente, un riel con su relleno.
 * Presentacional puro — no sabe de vehículos ni de fechas; quien lo usa decide qué
 * significa el `tone` y si hay `ratio`. Es el patrón que estaba copiado a mano en
 * `VehiclesPage` (el único de su tipo en `src/`).
 */
export function MeterBar({ label, caption, ratio, tone, icon, hint }: MeterBarProps) {
  const percent = ratio === undefined ? undefined : Math.round(Math.min(Math.max(ratio, 0), 1) * 100);

  return (
    <div className={clsx('meter', `meter--${tone}`)}>
      <div className="meter__head">
        <span className="meter__label">{icon}{label}</span>
        <span className="meter__caption">{caption}</span>
      </div>
      {percent !== undefined && (
        <div
          className="meter__rail"
          role="meter"
          aria-label={`${label}: ${caption}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <div className="meter__fill" style={{ width: `${percent}%` }} />
        </div>
      )}
      {hint && <p className="meter__hint">{hint}</p>}
    </div>
  );
}
